import { SELECT_UNIQUE_ID_PREFIX } from "@faire/slate/components/select/consts";
import { componentCls } from "@faire/slate/lib/tracking";
import { Labelled } from "@faire/slate/private/form/Labelled";
import { buildFormIds } from "@faire/slate/private/form/utils";
import { SelectBase } from "@faire/slate/private/select/SelectBase";
import { ISelectWebProps } from "@faire/slate/private/select/types";
import type {
  IText,
  IDisableableOption,
} from "@faire/slate/private/shared/types";
import React from "react";

/**
 * The option type for the select component.
 */
export type ISelectOption<ValueType extends string = string> =
  React.OptionHTMLAttributes<HTMLOptionElement> & IDisableableOption<ValueType>;

type IProps<ValueType extends string> = {
  /**
   * Whether the select component is disabled.
   */
  disabled?: boolean;

  /**
   * Controls the error state of the select component.
   * When error is a `string` or `<StrictLocalMsg>` or `<ServerSideLocalMsg>`, error content
   * is displayed in the helper text.
   */
  error?: boolean | IText;

  /**
   * The helper text of the select component.
   */
  helper?: IText;
  /**
   * The label of the select component.
   */
  label?: string;

  /**
   * Whether to append "(optional)" text to the select label.
   *
   * @default false
   */
  optional?: boolean;

  /**
   * The options of the select component.
   */
  options: Array<ISelectOption<ValueType>>;

  /**
   * The placeholder of the select component.
   */
  placeholder?: string;

  /**
   * The tooltip text for the tooltip in the label.
   */
  tooltipText?: IText;

  /**
   * The z-index of the label tooltip.
   *
   * @default 5
   */
  tooltipZIndex?: number;

  /**
   * The value of the select component.
   */
  value?: string;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISelectWebProps;

/**
 * A Select helps users make a simple selection from a list of choices.
 *
 * > **Nested options are not supported, by design.** Grouped options are not implemented yet. There is no max-selection equivalent. See the [migration guide](migration-guide.mdx).
 *
 * {@link https://www.notion.so/faire/Select-2242efb5c25a8072b1b1ccc1706cc78b | Slate/Select}
 */
export const Select = <ValueType extends string = string>({
  disabled,
  error,
  helper,
  label,
  options,
  placeholder,
  id: idProp,
  ref,
  tooltipText,
  tooltipZIndex,
  optional = false,
  ...props
}: IProps<ValueType>) => {
  const { id, helperId } = buildFormIds({
    prefix: SELECT_UNIQUE_ID_PREFIX,
    idProp,
    name: props.name,
    label,
    helper,
    error,
  });

  return (
    <Labelled
      className={componentCls()}
      data-component-name="Slate/Select"
      disabled={disabled}
      error={error}
      helper={helper}
      helperId={helperId}
      label={label}
      inputId={id}
      tooltipText={tooltipText}
      tooltipZIndex={tooltipZIndex}
      optional={optional}
    >
      <SelectBase
        aria-describedby={helperId}
        disabled={disabled}
        error={error}
        id={id}
        placeholder={placeholder}
        ref={ref}
        {...props}
      >
        {options.map(({ value, label, ...option }) => (
          <option key={value} value={value} {...option}>
            {label}
          </option>
        ))}
      </SelectBase>
    </Labelled>
  );
};
